import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import apiService from '../../../services/api.service';
import ExhibitionTable from '../../../components/ExhibitionTable';
import './ExhibitionList.css';

const ExhibitionList = () => {
  const navigate = useNavigate();
  const [exhibitions, setExhibitions] = useState([]);
  const [filteredExhibitions, setFilteredExhibitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [successMessage, setSuccessMessage] = useState('');
  const exhibitionsPerPage = 10;

  useEffect(() => {
    fetchExhibitions();
  }, []);

  useEffect(() => {
    filterExhibitions();
  }, [exhibitions, searchTerm, statusFilter]);

  const fetchExhibitions = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getAdminExhibitions();
      if (response.data.success) {
        setExhibitions(response.data.data);
      } else {
        setError('Failed to load exhibitions');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load exhibitions');
      console.error('Exhibitions fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const filterExhibitions = () => {
    let filtered = [...exhibitions];

    // Apply search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(exhibition => 
        exhibition.event_name?.toLowerCase().includes(searchLower) ||
        exhibition.event_location?.toLowerCase().includes(searchLower) ||
        exhibition.event_caption?.toLowerCase().includes(searchLower)
      );
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(exhibition => 
        String(exhibition.status) === statusFilter
      );
    }

    setFilteredExhibitions(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  };

  const handleDelete = async (exhibitionId) => {
    try {
      setError(null);
      const response = await apiService.deleteExhibition(exhibitionId);
      if (response.data.success) {
        setSuccessMessage('Exhibition deleted successfully!');
        setTimeout(() => setSuccessMessage(''), 3000);
        fetchExhibitions(); // Refresh the list
      } else {
        setError('Failed to delete exhibition');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete exhibition');
      console.error('Exhibition delete error:', err);
    }
  };

  const handleEdit = (exhibitionId) => {
    navigate(`/admin/exhibitions/edit/${exhibitionId}`);
  };

  const handleAddNew = () => {
    navigate('/admin/exhibitions/new');
  };

  // Pagination logic
  const indexOfLastExhibition = currentPage * exhibitionsPerPage;
  const indexOfFirstExhibition = indexOfLastExhibition - exhibitionsPerPage;
  const currentExhibitions = filteredExhibitions.slice(indexOfFirstExhibition, indexOfLastExhibition);
  const totalPages = Math.ceil(filteredExhibitions.length / exhibitionsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pages = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

    if (endPage - startPage + 1 < maxPagesToShow) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }

    if (startPage > 1) {
      pages.push(
        <button key="first" onClick={() => paginate(1)} className="page-btn">
          1
        </button>
      );
      if (startPage > 2) {
        pages.push(<span key="dots1" className="page-dots">...</span>);
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => paginate(i)}
          className={`page-btn ${currentPage === i ? 'active' : ''}`}
        >
          {i}
        </button>
      );
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pages.push(<span key="dots2" className="page-dots">...</span>);
      }
      pages.push(
        <button key="last" onClick={() => paginate(totalPages)} className="page-btn">
          {totalPages}
        </button>
      );
    }

    return pages;
  };

  return (
    <div className="exhibition-list-container">
      <div className="exhibition-list-header">
        <h2>Exhibition Management</h2>
        <button className="btn btn-primary" onClick={handleAddNew}>
          + Add New Exhibition
        </button>
      </div>

      {error && (
        <div className="alert alert-error">
          <strong>Error:</strong> {error}
        </div>
      )}

      {successMessage && (
        <div className="alert alert-success">
          <strong>Success:</strong> {successMessage}
        </div>
      )}

      <div className="exhibition-list-controls">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search exhibitions by name, location, or caption..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>

        <div className="filter-box">
          <label htmlFor="statusFilter">Status:</label>
          <select
            id="statusFilter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">All</option>
            <option value="1">Active</option>
            <option value="0">Inactive</option>
          </select>
        </div>
      </div>

      <div className="exhibition-list-stats">
        <span>
          Showing {currentExhibitions.length > 0 ? indexOfFirstExhibition + 1 : 0} - {Math.min(indexOfLastExhibition, filteredExhibitions.length)} of {filteredExhibitions.length} exhibitions
          {searchTerm || statusFilter !== 'all' ? ` (filtered from ${exhibitions.length} total)` : ''}
        </span>
      </div>

      <ExhibitionTable
        exhibitions={currentExhibitions}
        onDelete={handleDelete}
        onEdit={handleEdit}
        loading={loading}
      />

      {totalPages > 1 && (
        <div className="pagination">
          <button
            className="page-btn"
            onClick={() => paginate(currentPage - 1)}
            disabled={currentPage === 1}
          >
            Previous
          </button>
          {renderPagination()}
          <button
            className="page-btn"
            onClick={() => paginate(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default ExhibitionList;

